import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { colors } from '@sweep-io/sweep-design';
import { RichTextEditorCtx, useRichTextEditor } from './RichTextEditorProvider';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextToolbar } from './RichTextToolbar';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { $getRoot } from 'lexical';
import { sharedContentStyles } from './richTextEditorStyles';
import { Placeholder } from './Placeholder';
import type { ToolbarItemType } from './toolbar-items/toolbarItemRegistry';

const DEFAULT_HTML_TOOLBAR_ITEMS: ToolbarItemType[] = [
  'HeadingSelector',
  'Separator',
  'Bold',
  'Italic',
  'Underline',
  'Link',
  'Separator',
  'OrderedList',
  'BulletList',
];

interface HtmlRichTextEditorProps {
  isReadOnly: boolean;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  onBlur: (value: string) => void;
  toolbarItems?: ToolbarItemType[];
}

function HtmlEditorContent({
  isReadOnly,
  value,
  placeholder,
  onChange,
  onBlur,
  toolbarItems = DEFAULT_HTML_TOOLBAR_ITEMS,
}: HtmlRichTextEditorProps) {
  const { editor } = useRichTextEditor();
  const initializedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (!value) return;

    editor.update(() => {
      const parser = new DOMParser();
      const dom = parser.parseFromString(value, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      const root = $getRoot();
      root.clear();
      root.append(...nodes);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    editor.setEditable(!isReadOnly);
  }, [editor, isReadOnly]);

  useEffect(() => {
    return editor.registerRootListener((rootElement, prevRootElement) => {
      const handleBlur = (e: FocusEvent) => {
        const relatedTarget = e.relatedTarget as HTMLElement;
        if (
          relatedTarget?.closest?.('.rich-text-toolbar') ||
          relatedTarget?.closest?.('.rich-text-editor-toolbar-item')
        ) {
          return;
        }

        editor.getEditorState().read(() => {
          const html = $generateHtmlFromNodes(editor);
          onBlurRef.current(html);
        });
      };
      if (prevRootElement) {
        prevRootElement.removeEventListener('blur', handleBlur);
      }
      if (rootElement) {
        rootElement.addEventListener('blur', handleBlur);
      }
    });
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor);
        onChangeRef.current(html);
      });
    });
  }, [editor]);

  return (
    <Box
      sx={{
        background: colors.white,
        border: `1px solid ${colors.grey[300]}`,
        borderRadius: '4px',
        '&:focus-within': {
          borderColor: colors.blue[500],
        },
      }}
    >
      {!isReadOnly && (
        <Box
          sx={{
            borderBottom: `1px solid ${colors.grey[300]}`,
            padding: '4px 8px',
            '& .rich-text-toolbar': {
              justifyContent: 'flex-start',
              '& svg': {
                width: '16px',
                height: '16px',
              },
            },
          }}
        >
          <RichTextToolbar items={toolbarItems} />
        </Box>
      )}

      <Box
        sx={{
          ...sharedContentStyles,
          position: 'relative',
          '& .lexical-text-underline': {
            textDecoration: 'underline',
          },
          '& .html-rich-text-editor': {
            outline: 'none',
            padding: '8px 12px',
            minHeight: '60px',
            fontFamily: 'Inter',
            fontSize: '12px',
            lineHeight: '1.42',
          },
        }}
      >
        <RichTextPlugin
          contentEditable={<ContentEditable className="html-rich-text-editor" />}
          placeholder={<Placeholder placeholder={placeholder} />}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <ListPlugin />
        <LinkPlugin attributes={{ target: '_blank', rel: 'noopener noreferrer' }} />
      </Box>
    </Box>
  );
}

const htmlEditorConfig = {
  namespace: 'HtmlRichTextEditor',
  onError: (error: Error) => {
    console.error('Lexical HtmlRichTextEditor error:', error);
  },
  nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode],
  theme: {
    text: {
      underline: 'lexical-text-underline',
    },
  },
};

const HtmlRichTextEditor = (props: HtmlRichTextEditorProps) => {
  const [urlEditorOpen, setUrlEditorOpen] = useState(false);
  const ctxValue = useMemo(
    () => ({ urlEditorOpen, setUrlEditorOpen, fontSizeSelectorScaleModifier: 1 }),
    [urlEditorOpen],
  );

  return (
    <RichTextEditorCtx.Provider value={ctxValue}>
      <LexicalComposer initialConfig={htmlEditorConfig}>
        <HtmlEditorContent {...props} />
      </LexicalComposer>
    </RichTextEditorCtx.Provider>
  );
};

export default HtmlRichTextEditor;
